import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import useInViewport from "@/components/editor/utils/use-in-viewport";
import {
  fetchSeriesEvents,
  getAllSeries,
  getIsGivenSeriesEventsLoadingStatus,
  getSeriesEvents,
} from "@/features/related-events/related-events-reducer";

const SeriesEventsStatusEnum = {
  LOADED: "loaded",
  LOADING: "loading",
  ERROR: "error",
};

const useSeriesEvents = (seriesExternalId: string) => {
  const dispatch = useDispatch();
  const { ref: viewportRef, isIntersecting: isInViewport } = useInViewport();
  const requestIdleCallbackId = useRef<number | null>(null);
  const allSeries = useSelector(getAllSeries);
  const seriesEvents = useSelector(getSeriesEvents);
  const seriesEventsLoadingStatus = useSelector((state) =>
    getIsGivenSeriesEventsLoadingStatus(state, seriesExternalId)
  );
  const prevSeriesExternalId = useRef<string>(seriesExternalId || "");
  const prevInViewport = useRef<boolean>(false);

  const allSeriesIds = useMemo(() => {
    const seriesIds = new Set();
    allSeries.forEach((series) => {
      seriesIds.add(series.externalId);
    });
    return seriesIds;
  }, [allSeries]);

  const isSeriesAvailable = useMemo(() => {
    return (
      seriesExternalId &&
      allSeriesIds.size > 0 &&
      allSeriesIds.has(seriesExternalId)
    );
  }, [allSeriesIds, seriesExternalId]);

  const isSeriesEventFetchNeeded = useCallback(
    (
      seriesStatus = [
        SeriesEventsStatusEnum.LOADED,
        SeriesEventsStatusEnum.LOADING,
      ]
    ) => {
      return (
        isSeriesAvailable && !seriesStatus.includes(seriesEventsLoadingStatus)
      );
    },
    [isSeriesAvailable, seriesEventsLoadingStatus]
  );

  const cancelIdleCallback = useCallback(() => {
    if (requestIdleCallbackId.current && "cancelIdleCallback" in window) {
      window.cancelIdleCallback(requestIdleCallbackId.current);
      requestIdleCallbackId.current = null;
    }
  }, []);

  const fetchSeriesEventsData = useCallback(() => {
    if (isSeriesEventFetchNeeded()) {
      cancelIdleCallback();
      dispatch(fetchSeriesEvents(seriesExternalId));
    }
  }, [
    isSeriesEventFetchNeeded,
    cancelIdleCallback,
    dispatch,
    seriesExternalId,
  ]);

  useEffect(() => {
    if (
      isSeriesEventFetchNeeded([
        SeriesEventsStatusEnum.LOADED,
        SeriesEventsStatusEnum.LOADING,
        SeriesEventsStatusEnum.ERROR,
      ]) &&
      requestIdleCallbackId.current === null &&
      "requestIdleCallback" in window
    ) {
      requestIdleCallbackId.current = window.requestIdleCallback(() => {
        fetchSeriesEventsData();
      });
    }

    return () => {
      cancelIdleCallback();
    };
  }, [cancelIdleCallback, fetchSeriesEventsData, isSeriesEventFetchNeeded]);

  useEffect(() => {
    if (isInViewport && !prevInViewport.current) {
      prevInViewport.current = true;
      fetchSeriesEventsData();
    }
  }, [isInViewport, fetchSeriesEventsData]);

  useEffect(() => {
    if (seriesExternalId && prevSeriesExternalId.current !== seriesExternalId) {
      prevSeriesExternalId.current = seriesExternalId;
      if (isSeriesEventFetchNeeded()) {
        fetchSeriesEventsData();
      }
    }
  }, [
    fetchSeriesEventsData,
    isSeriesEventFetchNeeded,
    seriesEventsLoadingStatus,
    seriesExternalId,
  ]);

  return {
    viewportRef,
    isInViewport,
    allSeriesIds,
    seriesEvents,
    seriesEventsLoadingStatus,
    isSeriesAvailable,
  };
};

export default useSeriesEvents;
